import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import api from '../api';
import { useAuth } from '../hooks/useAuth';
import { Navigate } from 'react-router-dom';
import { NOTIFICATION_EVENTS } from '../constants/notificationEvents';
import SearchableSelect from '../components/SearchableSelect';
import FilterSearchField from '../components/FilterSearchField';
import EmailAlertPreviewModal, { EmailAlertViewButton } from '../components/EmailAlertPreviewModal';
import ExpertiseAreasMaster from '../components/ExpertiseAreasMaster';
import {
  EMAIL_TEMPLATE_DEFAULTS,
  fillAllEmailTemplates,
  resolveTemplateContent
} from '../constants/emailTemplateDefaults';

/** Active portal settings tabs (Company / Masters / ID formats relocated or retired). */
const ALL_TABS = [
  // { id: 'company', label: 'Company' },
  { id: 'email', label: 'Email / SMTP' },
  { id: 'templates', label: 'Email templates' },
  { id: 'emailAlerts', label: 'Email Alerts' },
  { id: 'notifications', label: 'Notifications' },
  // { id: 'ids', label: 'ID formats' }, // → Hiring settings → ID formats
  // { id: 'masters', label: 'Masters' },
];

const MASK = '********';

const EMAIL_ALERT_SEARCH_COLUMNS = [
  { id: 'all', label: 'All columns' },
  { id: 'when', label: 'When' },
  { id: 'event', label: 'Event' },
  { id: 'to', label: 'To' },
  { id: 'subject', label: 'Subject' },
  { id: 'status', label: 'Status' },
  { id: 'body', label: 'Email body' }
];

function Toggle({ checked, onChange, label }) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 ds-body text-[hsl(var(--ds-fg))]">
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-[hsl(var(--ds-border))] accent-[hsl(var(--ds-primary))]"
        checked={!!checked}
        onChange={e => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

const EMPTY_SETTINGS = {
  company: { name: '', legalName: '', tagline: '', email: '', phone: '', website: '', address: '', city: '', state: '', country: 'India', logoPath: '', whatsappStatusNumber: '' },
  smtp: { enabled: false, host: '', port: 587, secure: false, user: '', pass: '', from: '', fromName: '' },
  whatsapp: { enabled: false, provider: 'twilio', accountSid: '', authToken: '', fromNumber: '', webhookUrl: '', defaultCountryCode: '91' },
  notificationRules: {},
  emailTemplates: {},
  idFormats: { application: 'App-000001', job: 'Job-000001', candidate: 'Cand-000001', client: 'Client-000001' },
  notificationEvents: []
};

export default function SettingsPage({
  visibleTabs = null,
  initialTab = null,
  embedded = false,
  hideTabBar = false,
}) {
  const { user, loading: authLoading } = useAuth();
  const qc = useQueryClient();
  const tabs = useMemo(() => {
    if (!visibleTabs?.length) return ALL_TABS;
    const allowed = new Set(visibleTabs);
    return ALL_TABS.filter(t => allowed.has(t.id));
  }, [visibleTabs]);
  const defaultTab = initialTab && tabs.some(t => t.id === initialTab)
    ? initialTab
    : tabs[0]?.id || 'email';
  const [tab, setTab] = useState(defaultTab);
  const [saveMsg, setSaveMsg] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [showSmtpPass, setShowSmtpPass] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('credentials');
  const [emailAlertSearch, setEmailAlertSearch] = useState('');
  const [emailAlertSearchCol, setEmailAlertSearchCol] = useState('all');
  const [previewEmailAlert, setPreviewEmailAlert] = useState(null);

  useEffect(() => {
    if (initialTab && tabs.some(t => t.id === initialTab)) {
      setTab(initialTab);
    }
  }, [initialTab, tabs]);

  const {
    data: settings,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get('/settings').then(r => r.data),
    enabled: !authLoading && user?.role === 'admin',
    retry: 1
  });

  const {
    data: templateCatalog,
    isLoading: templatesLoading,
    isError: templatesError,
    error: templatesQueryError
  } = useQuery({
    queryKey: ['settings-email-templates'],
    queryFn: async () => {
      try {
        const r = await api.get('/settings/email-templates');
        return r.data;
      } catch (err) {
        if (err.response?.status === 404 && settings?.emailTemplateCatalog) {
          return settings.emailTemplateCatalog;
        }
        if (err.response?.status === 404) {
          const s = await api.get('/settings');
          if (s.data?.emailTemplateCatalog) return s.data.emailTemplateCatalog;
        }
        throw err;
      }
    },
    enabled: !authLoading && user?.role === 'admin',
    retry: 1
  });

  const { data: emailAlerts = [], isLoading: emailAlertsLoading } = useQuery({
    queryKey: ['settings-email-alerts'],
    queryFn: () => api.get('/settings/email-alerts').then(r => r.data),
    enabled: !authLoading && user?.role === 'admin',
    refetchInterval: () => (document.visibilityState === 'visible' ? 30000 : false)
  });

  const [form, setForm] = useState(null);
  const formDirtyRef = useRef(false);

  const mergeSettingsIntoForm = (data, catalogTemplates = templateCatalog?.templates) => {
    const merged = {
      ...EMPTY_SETTINGS,
      ...data,
      company: { ...EMPTY_SETTINGS.company, ...data?.company },
      smtp: { ...EMPTY_SETTINGS.smtp, ...data?.smtp },
      whatsapp: { ...EMPTY_SETTINGS.whatsapp, ...data?.whatsapp },
      notificationRules: {
        ...EMPTY_SETTINGS.notificationRules,
        ...data?.notificationRules
      },
      idFormats: { ...EMPTY_SETTINGS.idFormats, ...data?.idFormats },
      emailTemplates: { ...(data?.emailTemplates || {}) }
    };
    for (const [key, tpl] of Object.entries(merged.emailTemplates)) {
      if (tpl && tpl.bodyHtml && !(tpl.bodyText || '').trim()) {
        merged.emailTemplates[key] = {
          subject: tpl.subject || '',
          bodyText: String(tpl.bodyHtml).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
        };
      }
    }
    merged.emailTemplates = fillAllEmailTemplates(merged.emailTemplates, catalogTemplates);
    return JSON.parse(JSON.stringify(merged));
  };

  useEffect(() => {
    if (settings && !formDirtyRef.current) {
      const catalogTemplates =
        templateCatalog?.templates || settings.emailTemplateCatalog?.templates;
      setForm(mergeSettingsIntoForm(settings, catalogTemplates));
    }
  }, [settings, templateCatalog?.templates, settings?.emailTemplateCatalog?.templates]);

  const saveSettings = useMutation({
    mutationFn: payload => api.put('/settings', payload).then(r => r.data),
    onSuccess: data => {
      formDirtyRef.current = false;
      const merged = mergeSettingsIntoForm(data, templateCatalog?.templates);
      setForm(merged);
      qc.setQueryData(['settings'], data);
      qc.invalidateQueries({ queryKey: ['poster-branding'] });
      setSaveMsg('Settings saved successfully.');
      setTimeout(() => setSaveMsg(''), 4000);
    },
    onError: err => {
      const msg = err?.response?.data?.message || err?.message || 'Save failed';
      setSaveMsg(`Save failed: ${msg}`);
    }
  });

  const uploadLogo = useMutation({
    mutationFn: file => {
      const fd = new FormData();
      fd.append('logo', file);
      return api.post('/settings/company/logo', fd).then(r => r.data);
    },
    onSuccess: data => {
      if (data?.settings) {
        qc.setQueryData(['settings'], data.settings);
      }
      if (data?.logoPath) {
        formDirtyRef.current = true;
        setForm(f =>
          f ? { ...f, company: { ...f.company, logoPath: data.logoPath } } : f
        );
        setSaveMsg('Logo uploaded. Click “Save all settings” to keep other changes in sync.');
        setTimeout(() => setSaveMsg(''), 5000);
      }
    },
    onError: err => {
      setSaveMsg(`Logo upload failed: ${err?.response?.data?.message || err?.message || 'unknown error'}`);
    }
  });

  const testSmtp = useMutation({
    mutationFn: to => {
      const smtpPayload = { ...(form?.smtp || {}) };
      if (smtpPayload.pass === MASK) delete smtpPayload.pass;
      return api
        .post('/settings/smtp/test', { to, smtp: smtpPayload }, { timeout: 35000 })
        .then(r => r.data);
    }
  });

  const events = useMemo(
    () =>
      (settings?.notificationEvents?.length ? settings.notificationEvents : NOTIFICATION_EVENTS),
    [settings?.notificationEvents]
  );

  const templateOptions = useMemo(() => {
    const fromApi = templateCatalog?.templates || [];
    if (fromApi.length) return fromApi;
    return NOTIFICATION_EVENTS.map(ev => {
      const def = EMAIL_TEMPLATE_DEFAULTS[ev.id] || {};
      return {
        eventType: ev.id,
        label: ev.label,
        group: ev.group,
        defaultSubject: def.subject || '',
        defaultBodyText: def.bodyText || '',
        buttonLabel: def.buttonLabel,
        buttonPath: def.buttonPath
      };
    });
  }, [templateCatalog?.templates]);

  const currentTpl = useMemo(
    () => templateOptions.find(t => t.eventType === selectedTemplate),
    [templateOptions, selectedTemplate]
  );

  const groupedEvents = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const g = ev.group || 'Other';
      if (!map[g]) map[g] = [];
      map[g].push(ev);
    }
    return map;
  }, [events]);

  const filteredEmailAlerts = useMemo(() => {
    const q = emailAlertSearch.trim().toLowerCase();
    if (!q) return emailAlerts;

    const fieldText = (row, col) => {
      const bodyPlain = (row.bodyHtml || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      switch (col) {
        case 'when':
          return row.createdAt ? new Date(row.createdAt).toLocaleString() : '';
        case 'event':
          return [row.eventType, row.audience].filter(Boolean).join(' ');
        case 'to':
          return Array.isArray(row.to) ? row.to.join(' ') : row.to || '';
        case 'subject':
          return row.subject || '';
        case 'status':
          return [row.status, row.error].filter(Boolean).join(' ');
        case 'body':
          return [bodyPlain, row.bodyText].filter(Boolean).join(' ');
        default:
          return [
            row.createdAt ? new Date(row.createdAt).toLocaleString() : '',
            row.eventType,
            row.audience,
            Array.isArray(row.to) ? row.to.join(' ') : row.to,
            row.subject,
            row.status,
            row.error,
            bodyPlain,
            row.bodyText
          ].join(' ');
      }
    };

    return emailAlerts.filter(row =>
      fieldText(row, emailAlertSearchCol).toLowerCase().includes(q)
    );
  }, [emailAlerts, emailAlertSearch, emailAlertSearchCol]);

  if (authLoading) return <div className="text-sm text-[hsl(var(--ds-muted-fg))] p-4">Loading…</div>;
  if (user?.role !== 'admin') return <Navigate to="/" replace />;

  if (isError) {
    const msg = error?.response?.data?.message || error?.message || 'Could not load settings';
    const is403 = error?.response?.status === 403;
    const is404 = error?.response?.status === 404;
    return (
      <div className="max-w-lg p-4 bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-danger)/0.35)]">
        <h1 className="text-lg font-semibold text-[hsl(var(--ds-fg))] mb-2">Settings</h1>
        <p className="text-sm text-red-600 mb-2">{msg}</p>
        {is404 && (
          <p className="text-xs text-[hsl(var(--ds-muted-fg))] mb-3">
            The API may be outdated. Restart the server: <code className="bg-[hsl(var(--ds-muted))] px-1">cd server && npm run dev</code>
          </p>
        )}
        {is403 && <p className="text-xs text-[hsl(var(--ds-muted-fg))] mb-3">Admin access is required.</p>}
        <button type="button" className="text-sm text-[hsl(var(--ds-primary))] underline" onClick={() => refetch()}>
          Retry
        </button>
      </div>
    );
  }

  if (isLoading || !form) return <div className="text-sm text-[hsl(var(--ds-muted-fg))] p-4">Loading settings…</div>;

  const patch = (section, key, value) => {
    formDirtyRef.current = true;
    setForm(f => ({ ...f, [section]: { ...f[section], [key]: value } }));
  };

  const patchRule = (eventId, channel, value) => {
    formDirtyRef.current = true;
    setForm(f => ({
      ...f,
      notificationRules: {
        ...f.notificationRules,
        [eventId]: { ...f.notificationRules?.[eventId], [channel]: value }
      }
    }));
  };

  const patchTemplateField = (eventType, field, value) => {
    formDirtyRef.current = true;
    setForm(f => ({
      ...f,
      emailTemplates: {
        ...f.emailTemplates,
        [eventType]: {
          ...f.emailTemplates?.[eventType],
          [field]: value
        }
      }
    }));
  };

  const resetTemplateToDefault = () => {
    if (!currentTpl) return;
    formDirtyRef.current = true;
    const defaults = resolveTemplateContent(selectedTemplate, {}, currentTpl);
    setForm(f => ({
      ...f,
      emailTemplates: {
        ...f.emailTemplates,
        [selectedTemplate]: {
          subject: defaults.subject,
          bodyText: defaults.bodyText
        }
      }
    }));
  };

  const activeTemplate = resolveTemplateContent(
    selectedTemplate,
    form.emailTemplates?.[selectedTemplate],
    currentTpl
  );

  const buildPayload = () => {
    const payload = {
      company: form.company,
      smtp: { ...form.smtp },
      whatsapp: { ...form.whatsapp },
      notificationRules: form.notificationRules,
      emailTemplates: form.emailTemplates,
      idFormats: form.idFormats
    };
    if (payload.smtp.pass === MASK) delete payload.smtp.pass;
    if (payload.whatsapp.authToken === MASK) delete payload.whatsapp.authToken;
    return payload;
  };

  const logoSrc = form.company?.logoPath || '';

  const showSaveBar = tab !== 'emailAlerts' && tab !== 'masters';

  return (
    <div className={embedded ? 'flex w-full min-h-0 flex-col' : 'mx-auto flex max-w-4xl min-h-0 flex-col'}>
      {!embedded && (
        <>
          <h1 className="ds-h3 mb-1 shrink-0 text-[hsl(var(--ds-fg))]">Project settings</h1>
          <p className="ds-caption mb-4 shrink-0 text-[hsl(var(--ds-muted-fg))]">
            Email delivery, templates, and per-event notifications (push = in-app bell, email, WhatsApp).
          </p>
        </>
      )}

      {!hideTabBar && tabs.length > 1 && (
        <div className="mb-4 flex shrink-0 flex-wrap gap-1 border-b border-[hsl(var(--ds-border))]">
          {tabs.map(t => (
            <button
              key={t.id}
              type="button"
              className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px ${
                tab === t.id ? 'border-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary))]' : 'border-transparent text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]'
              }`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {showSaveBar && (
        <div className="sticky top-0 z-10 mb-3 flex shrink-0 items-center gap-3 border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-4 py-3 shadow-[var(--ds-shadow-card)]">
          <button
            type="button"
            className="rounded-md border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary))] px-4 py-2 text-sm font-medium text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-50"
            disabled={saveSettings.isLoading}
            onClick={() => saveSettings.mutate(buildPayload())}
          >
            {saveSettings.isLoading ? 'Saving…' : 'Save all settings'}
          </button>
          {saveMsg && (
            <span
              className={`ds-body max-w-md ${
                saveMsg.toLowerCase().includes('fail') ? 'text-red-600' : 'text-green-600'
              }`}
            >
              {saveMsg}
            </span>
          )}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto pb-2">

      {tab === 'company' && (
        <section className="space-y-3 rounded-xl bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] shadow-[var(--ds-shadow-card)] p-4">
          <div className="flex items-start gap-4">
            <div className="w-24 h-24 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] flex items-center justify-center overflow-hidden shrink-0">
              {logoSrc ? (
                <img src={logoSrc} alt="Company logo" className="max-w-full max-h-full object-contain" />
              ) : (
                <span className="text-[10px] text-[hsl(var(--ds-muted-fg))] text-center px-1">No logo</span>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-[hsl(var(--ds-muted-fg))] mb-1">Company logo</label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="text-xs"
                onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) uploadLogo.mutate(file);
                }}
              />
              {uploadLogo.isLoading && <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">Uploading logo…</p>}
              {uploadLogo.isSuccess && <p className="text-xs text-green-600 mt-1">Logo file saved.</p>}
              {uploadLogo.isError && (
                <p className="text-xs text-red-600 mt-1">
                  {uploadLogo.error?.response?.data?.message || 'Logo upload failed'}
                </p>
              )}
            </div>
          </div>
          {[
            ['name', 'Company name'],
            ['legalName', 'Legal name'],
            ['tagline', 'Tagline'],
            ['email', 'Contact email'],
            ['phone', 'Contact phone'],
            ['whatsappStatusNumber', 'WhatsApp number (hiring poster share)'],
            ['website', 'Website'],
            ['address', 'Address'],
            ['city', 'City'],
            ['state', 'State'],
            ['country', 'Country']
          ].map(([key, label]) => (
            <div key={key} className="field-with-label">
              <input
                className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                placeholder=" "
                value={form.company?.[key] || ''}
                onChange={e => patch('company', key, e.target.value)}
              />
              <label>{label}</label>
            </div>
          ))}
        </section>
      )}

      {tab === 'email' && (
        <section
          className={
            embedded
              ? 'space-y-3 p-4'
              : 'space-y-3 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4 shadow-[var(--ds-shadow-card)]'
          }
        >
          <Toggle
            checked={form.smtp?.enabled}
            onChange={v => patch('smtp', 'enabled', v)}
            label="Use SMTP settings below (otherwise use server .env)"
          />
          {[
            ['host', 'SMTP host'],
            ['port', 'Port'],
            ['user', 'Username'],
            ['pass', 'Password (leave ******** to keep)'],
            ['from', 'From email'],
            ['fromName', 'From name']
          ].map(([key, label]) => (
            <label key={key} className="block">
              <span className="ds-caption text-[hsl(var(--ds-muted-fg))]">{label}</span>
              {key === 'pass' ? (
                <div className="relative mt-1">
                  <input
                    className="h-9 w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 pr-10 text-sm text-[hsl(var(--ds-fg))]"
                    type={showSmtpPass ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={form.smtp?.pass ?? ''}
                    onChange={e => patch('smtp', 'pass', e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
                    aria-label={showSmtpPass ? 'Hide password' : 'Show password'}
                    onClick={() => setShowSmtpPass(v => !v)}
                  >
                    {showSmtpPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              ) : (
                <input
                  className="mt-1 h-9 w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))]"
                  type={key === 'port' ? 'number' : 'text'}
                  value={form.smtp?.[key] ?? ''}
                  onChange={e =>
                    patch('smtp', key, key === 'port' ? Number(e.target.value) || 587 : e.target.value)
                  }
                />
              )}
            </label>
          ))}
          <Toggle
            checked={form.smtp?.secure}
            onChange={v => patch('smtp', 'secure', v)}
            label="Use TLS/SSL (secure)"
          />
          <div className="border-t border-[hsl(var(--ds-border))] pt-3">
            <p className="ds-caption mb-2 font-semibold text-[hsl(var(--ds-muted-fg))]">Send test email</p>
            <div className="flex gap-2">
              <input
                className="h-9 min-w-0 flex-1 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))]"
                placeholder="recipient@example.com"
                value={testEmail}
                onChange={e => setTestEmail(e.target.value)}
              />
              <button
                type="button"
                className="rounded-md border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm font-medium text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)]"
                disabled={testSmtp.isLoading}
                onClick={() => testSmtp.mutate(testEmail)}
              >
                {testSmtp.isLoading ? 'Sending…' : 'Test SMTP'}
              </button>
            </div>
            {testSmtp.isSuccess && (
              <p className="ds-caption mt-1 text-green-600">{testSmtp.data?.message}</p>
            )}
            {testSmtp.isError && (
              <p className="ds-caption mt-1 text-red-600">
                {testSmtp.error?.response?.data?.error ||
                  testSmtp.error?.response?.data?.message ||
                  'Test failed'}
              </p>
            )}
            <p className="ds-caption mt-3 text-[hsl(var(--ds-muted-fg))]">
              <strong>Microsoft 365:</strong> host <code className="text-xs">smtp.office365.com</code>, port{' '}
              <strong>587</strong>, TLS/SSL <strong>off</strong> (STARTTLS). SMTP AUTH must be enabled in Exchange
              admin.
              <br />
              <strong>GoDaddy:</strong> host <code className="text-xs">smtpout.secureserver.net</code>, port{' '}
              <strong>465</strong> with TLS/SSL on, or port <strong>587</strong> with TLS/SSL off. Username and From
              must match the mailbox.
            </p>
          </div>
        </section>
      )}

      {tab === 'templates' && (
        <section className="rounded-xl bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] shadow-[var(--ds-shadow-card)] p-4 space-y-3">
          <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
            Select a notification type — subject and message fill in automatically. Edit the plain text if needed, then
            save. When emails are sent, the system adds your company branding, footer, and an action button (e.g. Sign in,
            View jobs).
          </p>
          {templatesLoading && <p className="text-xs text-[hsl(var(--ds-muted-fg))]">Loading templates…</p>}
          {templatesError && (
            <p className="text-xs text-[hsl(var(--ds-warning-fg))] bg-[hsl(var(--ds-warning)/0.12)] border border-[hsl(var(--ds-warning)/0.35)] px-2 py-1.5">
              Could not load templates from server (
              {templatesQueryError?.response?.data?.message ||
                templatesQueryError?.message ||
                'unknown error'}
              ). Showing built-in defaults — restart the API if this persists.
            </p>
          )}
          <div className="field-with-label">
            <SearchableSelect
              items={templateOptions}
              value={selectedTemplate}
              onChange={setSelectedTemplate}
              getValue={tpl => tpl.eventType}
              getLabel={tpl => {
                const override = form?.emailTemplates?.[tpl.eventType] || {};
                return override.subject || tpl.defaultSubject || `[${tpl.group}] ${tpl.label}`;
              }}
              getSearchText={tpl => {
                const override = form?.emailTemplates?.[tpl.eventType] || {};
                return [
                  tpl.eventType,
                  tpl.group,
                  tpl.label,
                  tpl.defaultSubject,
                  tpl.defaultBodyText,
                  override.subject,
                  override.bodyText
                ]
                  .filter(Boolean)
                  .join('\n')
                  .toLowerCase();
              }}
              renderSelected={tpl => {
                const override = form?.emailTemplates?.[tpl.eventType] || {};
                return override.subject || tpl.defaultSubject || `[${tpl.group}] ${tpl.label}`;
              }}
              renderOption={tpl => {
                const override = form?.emailTemplates?.[tpl.eventType] || {};
                const subject = override.subject || tpl.defaultSubject || `[${tpl.group}] ${tpl.label}`;
                return (
                  <div className="min-w-0">
                    <div className="truncate text-[hsl(var(--ds-fg))]">{subject}</div>
                    <div className="truncate text-[11px] text-[hsl(var(--ds-muted-fg))]">
                      [{tpl.group}] {tpl.label} · {tpl.eventType}
                    </div>
                  </div>
                );
              }}
              placeholder="Search templates…"
              noResultsMessage="No templates match"
              clearAriaLabel="Clear template selection"
            />
            <label>Notification type</label>
          </div>
          {currentTpl?.buttonLabel && (
            <p className="text-[10px] text-[hsl(var(--ds-muted-fg))] bg-[hsl(var(--ds-muted))] border border-[hsl(var(--ds-border))] px-2 py-1.5">
              Email button: <strong>{currentTpl.buttonLabel}</strong>
              {currentTpl.buttonPath ? ` → ${currentTpl.buttonPath}` : ''}
            </p>
          )}
          <button
            type="button"
            className="text-xs text-[hsl(var(--ds-primary))] hover:underline"
            onClick={resetTemplateToDefault}
          >
            Reset this template to default text
          </button>
          <div className="field-with-label">
            <input
              className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
              placeholder=" "
              value={activeTemplate.subject}
              onChange={e => patchTemplateField(selectedTemplate, 'subject', e.target.value)}
            />
            <label>Email subject</label>
          </div>
          <div>
            <label className="text-xs font-medium text-[hsl(var(--ds-muted-fg))]">Message (plain text)</label>
            <textarea
              className="w-full min-h-[240px] rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))] resize-y mt-1 leading-relaxed"
              value={activeTemplate.bodyText}
              onChange={e => patchTemplateField(selectedTemplate, 'bodyText', e.target.value)}
              placeholder="Select a notification type above"
            />
          </div>
          <p className="text-[10px] text-[hsl(var(--ds-muted-fg))]">
            Use placeholders such as {'{{candidateName}}'}, {'{{jobTitle}}'}, {'{{loginUrl}}'} — replaced when the email is
            sent. Variables: {(templateCatalog?.variables || []).join(', ')}
          </p>
        </section>
      )}

      {tab === 'emailAlerts' && (
        <section
          className={
            embedded
              ? 'overflow-hidden'
              : 'overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]'
          }
        >
          <div className="space-y-3 border-b border-[hsl(var(--ds-border))] px-4 py-3">
            {!embedded ? (
              <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                Search any column; click the eye next to <strong>Sent</strong> to read the message that was sent.
              </p>
            ) : null}
            <div className="flex max-w-3xl flex-col gap-2 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1">
                <FilterSearchField
                  value={emailAlertSearch}
                  onChange={e => setEmailAlertSearch(e.target.value)}
                  placeholder="Search email alerts…"
                />
              </div>
              <select
                className="h-9 shrink-0 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] sm:w-40"
                value={emailAlertSearchCol}
                onChange={e => setEmailAlertSearchCol(e.target.value)}
                aria-label="Search column"
              >
                {EMAIL_ALERT_SEARCH_COLUMNS.map(col => (
                  <option key={col.id} value={col.id}>
                    {col.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {emailAlertsLoading ? (
            <div className="p-4 ds-body text-[hsl(var(--ds-muted-fg))]">Loading email alerts…</div>
          ) : emailAlerts.length === 0 ? (
            <div className="p-4 ds-body text-[hsl(var(--ds-muted-fg))]">No email alerts yet.</div>
          ) : filteredEmailAlerts.length === 0 ? (
            <div className="p-4 ds-body text-[hsl(var(--ds-muted-fg))]">No alerts match your search.</div>
          ) : (
            <div>
              <p className="ds-caption border-b border-[hsl(var(--ds-border))] px-4 py-2 text-[hsl(var(--ds-muted-fg))]">
                Showing {filteredEmailAlerts.length} of {emailAlerts.length}
              </p>
              <div className="overflow-auto">
                <table className="ds-table w-full text-left text-sm">
                  <thead>
                    <tr>
                      <th>Sl No</th>
                      <th>When</th>
                      <th>Event</th>
                      <th>To</th>
                      <th>Subject</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmailAlerts.map((row, idx) => (
                      <tr key={row.id || `${row.createdAt}-${idx}`}>
                        <td className="text-[hsl(var(--ds-muted-fg))]">{idx + 1}</td>
                        <td className="whitespace-nowrap">
                          {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                        </td>
                        <td>{row.eventType || '—'}</td>
                        <td className="break-all">
                          {Array.isArray(row.to) ? row.to.join(', ') : row.to || '—'}
                        </td>
                        <td>{row.subject || '—'}</td>
                        <td>
                          <EmailAlertViewButton row={row} onView={setPreviewEmailAlert} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      )}

      {previewEmailAlert && (
        <EmailAlertPreviewModal alert={previewEmailAlert} onClose={() => setPreviewEmailAlert(null)} />
      )}

      {tab === 'notifications' && (
        <section
          className={
            embedded
              ? 'overflow-hidden'
              : 'overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]'
          }
        >
          <p className="ds-caption border-b border-[hsl(var(--ds-border))] px-4 py-3 text-[hsl(var(--ds-muted-fg))]">
            Push = in-app notification bell. Email / WhatsApp use hiring portal branding on send.
          </p>
          <div className="overflow-auto">
            <table className="ds-table w-full text-left text-sm">
              <thead>
                <tr>
                  <th className="w-16">Sl No</th>
                  <th>Event</th>
                  <th className="w-20 text-center">Push</th>
                  <th className="w-20 text-center">Email</th>
                  <th className="w-24 text-center">WhatsApp</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(groupedEvents).map(([group, evs]) => (
                  <Fragment key={group}>
                    <tr>
                      <td
                        colSpan={5}
                        className="bg-[hsl(var(--ds-muted))] ds-caption font-semibold text-[hsl(var(--ds-muted-fg))]"
                      >
                        {group}
                      </td>
                    </tr>
                    {evs.map((ev, idx) => {
                      const rule = form.notificationRules?.[ev.id] || {};
                      return (
                        <tr key={ev.id}>
                          <td className="text-[hsl(var(--ds-muted-fg))]">{idx + 1}</td>
                          <td className="ds-body">{ev.label}</td>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-[hsl(var(--ds-border))] accent-[hsl(var(--ds-primary))]"
                              checked={rule.push !== false}
                              onChange={e => patchRule(ev.id, 'push', e.target.checked)}
                            />
                          </td>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-[hsl(var(--ds-border))] accent-[hsl(var(--ds-primary))]"
                              checked={rule.email !== false}
                              onChange={e => patchRule(ev.id, 'email', e.target.checked)}
                            />
                          </td>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-[hsl(var(--ds-border))] accent-[hsl(var(--ds-primary))]"
                              checked={rule.whatsapp === true}
                              onChange={e => patchRule(ev.id, 'whatsapp', e.target.checked)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div className="space-y-3 border-t border-[hsl(var(--ds-border))] p-4">
            <h3 className="ds-h4">WhatsApp delivery</h3>
            <Toggle
              checked={form.whatsapp?.enabled}
              onChange={v => patch('whatsapp', 'enabled', v)}
              label="Enable WhatsApp notifications"
            />
            <label className="block">
              <span className="ds-caption text-[hsl(var(--ds-muted-fg))]">Provider</span>
              <select
                className="mt-1 h-9 w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))]"
                value={form.whatsapp?.provider || 'twilio'}
                onChange={e => patch('whatsapp', 'provider', e.target.value)}
              >
                <option value="twilio">Twilio WhatsApp API</option>
                <option value="webhook">Custom webhook</option>
              </select>
            </label>
            {form.whatsapp?.provider === 'webhook' ? (
              <label className="block">
                <span className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                  Webhook URL (POST JSON: to, message, company)
                </span>
                <input
                  className="mt-1 h-9 w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))]"
                  value={form.whatsapp?.webhookUrl || ''}
                  onChange={e => patch('whatsapp', 'webhookUrl', e.target.value)}
                />
              </label>
            ) : (
              <>
                {[
                  ['accountSid', 'Twilio Account SID'],
                  ['authToken', 'Auth token (******** to keep)'],
                  ['fromNumber', 'From WhatsApp number (e.g. +14155238886)']
                ].map(([key, label]) => (
                  <label key={key} className="block">
                    <span className="ds-caption text-[hsl(var(--ds-muted-fg))]">{label}</span>
                    <input
                      className="mt-1 h-9 w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))]"
                      type={key === 'authToken' ? 'password' : 'text'}
                      value={form.whatsapp?.[key] || ''}
                      onChange={e => patch('whatsapp', key, e.target.value)}
                    />
                  </label>
                ))}
              </>
            )}
            <label className="block">
              <span className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                Default country code for 10-digit phones
              </span>
              <input
                className="mt-1 h-9 w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))]"
                value={form.whatsapp?.defaultCountryCode || '91'}
                onChange={e => patch('whatsapp', 'defaultCountryCode', e.target.value)}
              />
            </label>
            <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
              Without Twilio/webhook credentials, messages are saved to the WhatsApp outbox. Phone numbers are
              resolved from user, candidate, recruiter, and client contact records.
            </p>
          </div>
        </section>
      )}

      {tab === 'ids' && (
        <section className="space-y-3 rounded-xl bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] shadow-[var(--ds-shadow-card)] p-4">
          {['application', 'job', 'candidate', 'client'].map(key => (
            <div key={key} className="field-with-label">
              <input
                className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                placeholder=" "
                value={form.idFormats?.[key] || ''}
                onChange={e => {
                  formDirtyRef.current = true;
                  setForm(f => ({
                    ...f,
                    idFormats: { ...f.idFormats, [key]: e.target.value }
                  }));
                }}
              />
              <label>{key.charAt(0).toUpperCase() + key.slice(1)} ID format</label>
            </div>
          ))}
        </section>
      )}

      {tab === 'masters' && (
        <section className="rounded-xl bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] shadow-[var(--ds-shadow-card)] p-4 rounded-xl">
          <ExpertiseAreasMaster />
        </section>
      )}
      </div>
    </div>
  );
}
